import { useEffect, useState, useCallback } from 'react';
import { Modal, Avatar, Rate, Image, Spin } from 'antd';
import { EnvironmentOutlined, UserOutlined, DownloadOutlined, DownOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { parseGalleryImages, parseQualifications, parseServices, parseCategories, getInitials, professionTypeLabelSingular } from '../hooks';
import './ProProfileModal.css';

// Note d'une review : score_global, sinon moyenne des 5 sous-critères
function reviewScore(r) {
  if (r.score_global != null) return Number(r.score_global);
  return (
    Number(r.qualite_travail || 0) +
    Number(r.respect_delais || 0) +
    Number(r.communication || 0) +
    Number(r.rapport_qualite_prix || 0) +
    Number(r.proprete_chantier || 0)
  ) / 5;
}

function formatDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

// Un avis : score total + flèche pour ouvrir/fermer le détail (fermé par défaut)
function ReviewItem({ r, reviewer }) {
  const [open, setOpen] = useState(false);
  const score = reviewScore(r);
  const imgs = Array.isArray(r.image_urls) ? r.image_urls : [];
  const criteria = [
    ['Compétences techniques', r.proprete_chantier],
    ['Qualité du travail', r.qualite_travail],
    ['Respect des délais', r.respect_delais],
    ['Rapport qualité/prix', r.rapport_qualite_prix],
    ['Communication et professionnalisme', r.communication],
  ];

  return (
    <div className="bl-review">
      <div className="bl-review-head">
        <Avatar size={36} src={reviewer.avatar} icon={!reviewer.avatar ? <UserOutlined /> : null} />
        <div className="bl-review-meta">
          <span className="bl-review-author">{reviewer.name}</span>
          <span className="bl-review-date">{formatDate(r.created_at)}</span>
        </div>

        {/* Score total */}
        <div className="bl-review-score">
          <Rate disabled allowHalf value={score} style={{ fontSize: 13 }} />
          <span className="bl-review-score-val">{score.toFixed(1)}/5</span>
        </div>

        {/* Flèche ouvrir / fermer le détail */}
        <button
          type="button"
          className={`bl-review-toggle${open ? ' open' : ''}`}
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? 'Masquer le détail' : 'Voir le détail'}
        >
          <DownOutlined />
        </button>
      </div>

      {/* Détail des critères (replié par défaut) */}
      {open && (
        <div className="bl-review-criteria">
          {criteria.map(([label, val]) => (
            <div className="bl-review-crit" key={label}>
              <span className="bl-review-crit-label">{label}</span>
              <span className="bl-review-crit-right">
                <Rate disabled allowHalf value={Number(val) || 0} style={{ fontSize: 12 }} />
                <span className="bl-review-crit-val">{(Number(val) || 0).toFixed(1)}</span>
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Messages positif / négatif avec smiley (rien si absent) */}
      {r.positives && (
        <div className="bl-review-msg pos">
          <span className="bl-review-emoji">😊</span>
          <p>{r.positives}</p>
        </div>
      )}
      {r.negatives && (
        <div className="bl-review-msg neg">
          <span className="bl-review-emoji">😞</span>
          <p>{r.negatives}</p>
        </div>
      )}

      {imgs.length > 0 && (
        <div className="bl-review-imgs">
          <Image.PreviewGroup>
            {imgs.map((u, i) => (
              <Image key={i} src={u} alt={`avis-${i}`} width={52} height={52} style={{ objectFit: 'cover', borderRadius: 6 }} />
            ))}
          </Image.PreviewGroup>
        </div>
      )}
    </div>
  );
}

function ProProfileModal({ open, pro, onClose }) {
  const history = useHistory();
  const [loading, setLoading] = useState(false);

  // Renvoie vers la section "Plus pratique avec l'application" (id app-mobile) de l'accueil
  const goToAppSection = () => {
    if (onClose) onClose();
    if (window.location.pathname !== '/') history.push('/');
    setTimeout(() => {
      const el = document.getElementById('app-mobile');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 350);
  };
  const [gallery, setGallery] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [services, setServices] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewers, setReviewers] = useState({});
  const [showAllReviews, setShowAllReviews] = useState(false);

  const load = useCallback(async () => {
    if (!pro) return;
    setLoading(true);
    setGallery([]);
    setQualifications([]);
    setServices([]);
    setKeywords([]);
    setReviews([]);
    setReviewers({});
    setShowAllReviews(false);
    try {
      // Galerie (max 4) + qualifications + services + mots-clés depuis pro_dashboard
      const { data: dash } = await supabase
        .from('pro_dashboard')
        .select('urlGallery, qualification, offered_services, keywords')
        .eq('user_id', pro.id)
        .limit(1);
      const d = dash?.[0] || {};
      setGallery(parseGalleryImages(d.urlGallery).slice(0, 4));
      setQualifications(parseQualifications(d.qualification).filter(Boolean));
      setServices(parseServices(d.offered_services).filter(Boolean));
      setKeywords(parseServices(d.keywords).filter(Boolean));

      // Avis depuis pro_reviews
      const { data: revs } = await supabase
        .from('pro_reviews')
        .select('*')
        .eq('target_user_id', pro.id)
        .order('created_at', { ascending: false });
      const list = revs || [];
      setReviews(list);

      // Noms des auteurs d'avis
      const reviewerIds = [...new Set(list.map((r) => r.reviewer_user_id).filter(Boolean))];
      if (reviewerIds.length) {
        const { data: rp } = await supabase
          .from('profiles')
          .select('id, firstname, lastname, imageavatar')
          .in('id', reviewerIds);
        const map = {};
        (rp || []).forEach((p) => {
          map[p.id] = {
            name: `${p.firstname || ''} ${p.lastname || ''}`.trim() || 'Utilisateur',
            avatar: p.imageavatar,
          };
        });
        setReviewers(map);
      }
    } catch (err) {
      console.error('[ProProfileModal]', err);
    } finally {
      setLoading(false);
    }
  }, [pro]);

  useEffect(() => {
    if (open && pro) load();
  }, [open, pro, load]);

  if (!pro) return null;

  const name = pro._name || `${pro.firstname || ''} ${pro.lastname || ''}`.trim() || 'Professionnel';
  const cats = parseCategories(pro.categories).filter(Boolean);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={680}
      title={null}
      className="bl-modal"
      styles={{ body: { maxHeight: '74vh', overflowY: 'auto', paddingRight: 8 } }}
    >
      {/* En-tête pro */}
      <div className="bl-modal-head">
        {pro.imageavatar ? (
          <Avatar size={64} src={pro.imageavatar} />
        ) : (
          <Avatar size={64} style={{ background: '#fdd119', color: '#1f2533', fontWeight: 700, fontSize: 24 }}>
            {getInitials(pro.firstname, pro.lastname)}
          </Avatar>
        )}
        <div className="bl-modal-head-info">
          <h2 className="bl-modal-name">{name}</h2>
          <div>          {pro.profession && <p className="bl-modal-job">{professionTypeLabelSingular(pro.profession)}</p>}

          {cats.length > 0 && (
            <div className="bl-modal-head-cats">
              {cats.map((c, i) => (
                <span key={i} className="bl-modal-head-cat">{c}</span>
              ))}
            </div>
          )}
          </div>
          {pro.governorate && (
            <p className="bl-modal-loc"><EnvironmentOutlined /> {pro.governorate}</p>
          )}
        </div>

        <button type="button" className="bl-modal-head-btn" onClick={goToAppSection}>
          <DownloadOutlined /> <span className="bl-modal-head-btn-label">Installer l'app</span>
        </button>
      </div>

      {loading ? (
        <div className="bl-modal-loading"><Spin /></div>
      ) : (
        <>
          {/* Galerie (masquée si vide) */}
          {gallery.length > 0 && (
            <div className="bl-modal-section">
              <h3 className="bl-modal-section-title">Galerie</h3>
              <div className="bl-modal-gallery">
                <Image.PreviewGroup>
                  {gallery.map((url, i) => (
                    <Image
                      key={i}
                      src={url}
                      alt={`galerie-${i}`}
                      className="bl-modal-gallery-img"
                    />
                  ))}
                </Image.PreviewGroup>
              </div>
            </div>
          )}

          {/* Qualifications (masquée si vide) */}
          {qualifications.length > 0 && (
            <div className="bl-modal-section">
              <h3 className="bl-modal-section-title">Qualifications</h3>
              <div className="bl-modal-tags">
                {qualifications.map((qa, i) => (
                  <span key={i} className="bl-modal-tag">{qa}</span>
                ))}
              </div>
            </div>
          )}

          {/* Services (masquée si vide) */}
          {services.length > 0 && (
            <div className="bl-modal-section">
              <h3 className="bl-modal-section-title">Services</h3>
              <div className="bl-modal-tags">
                {services.map((sv, i) => (
                  <span key={i} className="bl-modal-tag bl-modal-tag--service">{sv}</span>
                ))}
              </div>
            </div>
          )}

          {/* Mots-clés (masquée si vide) */}
          {keywords.length > 0 && (
            <div className="bl-modal-section">
              <h3 className="bl-modal-section-title">Mots-clés</h3>
              <div className="bl-modal-tags">
                {keywords.map((kw, i) => (
                  <span key={i} className="bl-modal-tag">{kw}</span>
                ))}
              </div>
            </div>
          )}

          {/* Avis (masquée si vide) */}
          {reviews.length > 0 && (
            <div className="bl-modal-section">
              <h3 className="bl-modal-section-title">Avis ({reviews.length})</h3>
              <div className="bl-modal-reviews">
                {(showAllReviews ? reviews : reviews.slice(0, 3)).map((r, idx) => (
                  <ReviewItem
                    key={r.id || idx}
                    r={r}
                    reviewer={reviewers[r.reviewer_user_id] || { name: 'Utilisateur', avatar: null }}
                  />
                ))}
              </div>

              {reviews.length > 3 && (
                <button
                  type="button"
                  className="bl-modal-more"
                  onClick={() => setShowAllReviews((v) => !v)}
                >
                  {showAllReviews ? 'Voir moins' : `Voir plus (${reviews.length - 3})`}
                </button>
              )}
            </div>
          )}

          {/* CTA : installer l'application pour plus de détails */}
          <div className="bl-modal-cta">
            <p className="bl-modal-cta-text">
              Pour plus de détails sur ce professionnel, installez l'application BATILINK.
            </p>
            <button type="button" className="bl-modal-cta-btn" onClick={goToAppSection}>
              <DownloadOutlined /> Installer l'application
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

export default ProProfileModal;
